import { Button, ButtonGroup, Typography } from '@mui/material';
import { Fragment } from 'react';
import { categoriesLabels, CategoryType } from 'src/constants/category';
import { AxisInput, PieceDetails, detailsInformations, detailsInputs, modes } from './types';
import { Container, Input, Label, Row, Select } from './styles';

type CategorySelectProps = {
  category: CategoryType;
  setCategory: (category: CategoryType) => void;
  style?: React.CSSProperties;
};

export const CategorySelect = ({ category, setCategory, style }: CategorySelectProps) => {
  return (
    <Select
      name="sens"
      id="sens"
      onChange={(e) => {
        setCategory(e.target.value as CategoryType);
      }}
      style={style}
    >
      {categoriesLabels.map((el) => {
        return (
          <option key={el.key} selected={category === el.key} value={el.key}>
            {el.key === 'ANY' ? 'Autre pièces' : null}
            {el.key === 'ANY_ACCESSORY' ? 'Autre accesoires' : null}
            {el.key !== 'ANY_ACCESSORY' && el.key !== 'ANY' ? el.label : null}
          </option>
        );
      })}
    </Select>
  );
};

type MaterialSelectProps = {
  material: 'GRANIT' | 'INOX';
  setMaterial: (material: 'GRANIT' | 'INOX') => void;
  style?: React.CSSProperties;
};

export const MaterialSelect = ({ material, setMaterial, style }: MaterialSelectProps) => {
  return (
    <Select
      name="material"
      id="material"
      onChange={(e) => {
        setMaterial(e.target.value as 'GRANIT' | 'INOX');
      }}
      style={style}
    >
      <option selected={material === 'GRANIT'} value="GRANIT">
        Granit
      </option>
      <option selected={material === 'INOX'} value="INOX">
        Inox
      </option>
    </Select>
  );
};

type BlockedAxisSelectorProps = {
  blockedAxis: string[];
  setBlockedAxis: (blockedAxis: string[]) => void;
};

export const BlockedAxisSelector = ({ blockedAxis, setBlockedAxis }: BlockedAxisSelectorProps) => {
  const toggleAxis = (axis: 'x' | 'y' | 'z') => {
    if (blockedAxis.includes(axis)) {
      setBlockedAxis(blockedAxis.filter((el) => el !== axis));
    } else {
      setBlockedAxis([...blockedAxis, axis]);
    }
  };

  return (
    <>
      <Typography variant="body1" style={{ fontSize: '12px', width: '100%' }}>
        Modifiable sur:
      </Typography>
      <ButtonGroup
        variant="contained"
        aria-label="outlined primary button group"
        sx={{ width: '100%', marginBottom: '8px' }}
      >
        <Button
          style={{ fontSize: '10px' }}
          onClick={() => toggleAxis('x')}
          variant={blockedAxis.includes('x') ? 'outlined' : 'contained'}
        >
          X
        </Button>
        <Button
          style={{ fontSize: '10px' }}
          onClick={() => toggleAxis('y')}
          variant={blockedAxis.includes('y') ? 'outlined' : 'contained'}
        >
          Y
        </Button>
        <Button
          style={{ fontSize: '10px' }}
          onClick={() => toggleAxis('z')}
          variant={blockedAxis.includes('z') ? 'outlined' : 'contained'}
        >
          z
        </Button>
      </ButtonGroup>
    </>
  );
};

type AxisInputGroupProps = {
  label: string;
  value: AxisInput;
  onChange: (value: AxisInput) => void;
};

export const AxisInputGroup = ({ label, value, onChange }: AxisInputGroupProps) => {
  return (
    <>
      <Typography variant="body1" style={{ fontSize: '12px', width: '100%' }}>
        {label}:
      </Typography>
      <Row
        style={{
          justifyContent: 'space-between',
          gap: '12px',
          marginBottom: '12px',
        }}
      >
        <Row style={{ gap: 8 }}>
          <Label>X</Label>
          <Input
            value={value.x}
            onChange={(e) => onChange({ ...value, x: e.target.value })}
          />
        </Row>
        <Row style={{ gap: 8 }}>
          <Label>Z</Label>
          <Row>
            <Input
              value={value.z}
              onChange={(e) => onChange({ ...value, z: e.target.value })}
            />
          </Row>
        </Row>
        <Row style={{ gap: 8 }}>
          <Label>Y</Label>
          <Row>
            <Input
              value={value.y}
              onChange={(e) => onChange({ ...value, y: e.target.value })}
            />
          </Row>
        </Row>
      </Row>
    </>
  );
};

type PieceDetailsFormProps = {
  details: {
    french: { linear: PieceDetails; m2: PieceDetails };
    import: { linear: PieceDetails; m2: PieceDetails };
  };
  extra: { french: string; import: string };
  minPrice?: { french: string; import: string };
  onChangeDetails: (
    country: 'french' | 'import',
    mode: 'linear' | 'm2',
    key: keyof PieceDetails,
    value: string,
  ) => void;
  onChangeExtra: (country: 'french' | 'import', value: string) => void;
  onChangeMinPrice?: (country: 'french' | 'import', value: string) => void;
};

export const PieceDetailsForm = ({
  details,
  extra,
  minPrice,
  onChangeDetails,
  onChangeExtra,
  onChangeMinPrice,
}: PieceDetailsFormProps) => {
  return (
    <>
      {detailsInformations.map((detailsInformation) => {
        return (
          <Container key={detailsInformation.key}>
            <Typography variant="h1" style={{ fontSize: '18px' }}>
              {detailsInformation.label}
            </Typography>
            <Row style={{ gap: 8, width: '100%' }}>
              <Label sx={{ width: 'fit-content' }}>Plus value</Label>
              <Input
                sx={{ flex: 1 }}
                value={extra[detailsInformation.key]}
                onChange={(e) => onChangeExtra(detailsInformation.key, e.target.value)}
              />
            </Row>
            {minPrice && onChangeMinPrice && (
              <Row style={{ gap: 8, width: '100%' }}>
                <Label sx={{ width: 'fit-content' }}>Prix minimum</Label>
                <Input
                  sx={{ flex: 1 }}
                  value={minPrice[detailsInformation.key]}
                  onChange={(e) =>
                    onChangeMinPrice(detailsInformation.key, e.target.value)
                  }
                />
              </Row>
            )}

            {modes.map((mode) => {
              return (
                <Fragment key={mode.key}>
                  <Typography variant="body1" style={{ fontSize: '12px', width: '100%' }}>
                    {mode.label}
                  </Typography>

                  <Row>
                    {detailsInputs.line1.map((input) => {
                      return (
                        <Row key={input.key} style={{ gap: 8 }}>
                          <Label>{input.label}</Label>
                          <Input
                            sx={{ flex: 1 }}
                            value={details[detailsInformation.key][mode.key][input.key]}
                            onChange={(e) =>
                              onChangeDetails(
                                detailsInformation.key,
                                mode.key,
                                input.key,
                                e.target.value,
                              )
                            }
                          />
                        </Row>
                      );
                    })}
                  </Row>
                  <Row>
                    {detailsInputs.line2.map((input) => {
                      return (
                        <Row key={input.key} style={{ gap: 8 }}>
                          <Label>{input.label}</Label>
                          <Input
                            sx={{ flex: 1 }}
                            value={details[detailsInformation.key][mode.key][input.key]}
                            onChange={(e) =>
                              onChangeDetails(
                                detailsInformation.key,
                                mode.key,
                                input.key,
                                e.target.value,
                              )
                            }
                          />
                        </Row>
                      );
                    })}
                  </Row>
                </Fragment>
              );
            })}
          </Container>
        );
      })}
    </>
  );
};
